// Replacement for `printElement` of original/src/components/ProfessionalPrint.tsx:2007-2235. The old helper
// copied the template's `outerHTML` into a hidden iframe with `document.write`, pulled Cairo from Google Fonts and
// re-serialised every stylesheet of the page. All of that is forbidden here (ADR-03 §2.4: no `document.write`,
// no `innerHTML`/`outerHTML`; D-146: fonts are self-hosted, no third-party requests from the app).
//
// Approach: the container wraps the rendered template in an element with `PRINT_ROOT_CLASS`; the print stylesheet
// (`print.css`, imported once by the web application — [C28] F-274: an injected `<style>` element is refused by the
// Content-Security-Policy `style-src 'self'`) carries the old `@page` rule (:2064-2067) and an `@media print` block that hides every other child of
// `<body>` and shows the print root at full width, together with the old print CSS for `.professional-page`,
// tables and rows (:2068-2102, :2155-2175). It then calls `window.print()` on the page itself, where the
// self-hosted fonts and the Tailwind stylesheet are already loaded. The old retry loop waiting for the
// element to mount (:2009-2016) is kept in spirit: the caller renders the template first, then calls this.
export const PRINT_ROOT_CLASS = 'faylasuf-print-root';

// The CSS copied from the old iframe template (:2063-2176) lives in print.css; selectors are scoped to the print root
// where they addressed `html, body` so that the application shell is only hidden, not restyled.

/** [C35] 80 mm paper (D-149): 80 mm wide, 4 mm margins — the 72 mm voucher fills the printable width. */
export const V80_PAGE_WIDTH_MM = 80;
export const V80_MARGIN_MM = 4;

/**
 * [C35] The page height of an 80 mm voucher: the rendered height of the voucher plus the margins, at least 60 mm —
 * thermal rolls have no fixed length, so the page ends where the voucher ends (no blank paper after it).
 */
export function v80PageHeightMm(heightPx: number): number {
  return Math.max(60, Math.ceil((heightPx * 25.4) / 96) + 2 * V80_MARGIN_MM + 2);
}

/**
 * Prints the document whose template root has `elementId` (normally `professional-print-template`). The element
 * must already be rendered inside a container carrying `PRINT_ROOT_CLASS`; if the template is not mounted or the
 * container is missing, nothing is printed (the old helper retried for 3 s, :2009-2016; here the caller renders
 * first and calls afterwards).
 *
 * [C35] `format: '80mm'` (D-149): the A4 `@page` rule of print.css is overridden for this one print by a constructed
 * stylesheet (`document.adoptedStyleSheets` — CSSOM, which the CSP `style-src 'self'` allows, unlike a `<style>`
 * element, F-274) with the 80 mm page sized to the voucher; it is removed after the print dialog closes.
 */
export function printDocument(elementId: string, opts: { format?: 'a4' | '80mm' } = {}): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const element = document.getElementById(elementId);
  if (!element) return;
  if (!element.closest(`.${PRINT_ROOT_CLASS}`)) return;
  if (opts.format !== '80mm' || typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in document)) {
    window.print();
    return;
  }
  const sheet = new CSSStyleSheet();
  const h = v80PageHeightMm(element.getBoundingClientRect().height);
  sheet.replaceSync(`@page { size: ${V80_PAGE_WIDTH_MM}mm ${h}mm; margin: ${V80_MARGIN_MM}mm !important; }`);
  const before = document.adoptedStyleSheets;
  document.adoptedStyleSheets = [...before, sheet];
  try {
    window.print();
  } finally {
    document.adoptedStyleSheets = document.adoptedStyleSheets.filter((x) => x !== sheet);
  }
}
